import axiosInstance from "./axiosConfig";

/**
 * Service for cart-related API calls
 */
const cartService = {
  /**
   * Get user's active cart
   * @returns {Promise<Object>} Cart data
   */
  getCart: async () => {
    try {
      const response = await axiosInstance.get("/cart");
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: "Error fetching cart" };
    }
  },

  /**
   * Add item to cart
   * @param {Object} item - Item to add
   * @param {string} item.productId - Product ID
   * @param {number} item.quantity - Quantity
   * @param {Object} item.options - Options for the item
   * @param {string} [item.options.variationId] - Variation ID (for variable products)
   * @param {boolean} [item.options.useVariationStock] - Whether to use variation stock
   * @param {string} [item.options.variationName] - Variation name
   * @param {Object} [item.options.attributes] - Selected attributes
   * @returns {Promise<Object>} Updated cart
   */
  addToCart: async (item) => {
    try {
      console.log("Cart service sending request:", item);
      const response = await axiosInstance.post("/cart/items", item);
      return response.data;
    } catch (error) {
      console.error(
        "Cart service error:",
        error.response?.data || error.message
      );
      // If it's a proper API error response with data, forward it
      if (error.response && error.response.data) {
        throw error.response.data;
      }
      // Otherwise create a generic error
      throw { message: "Error adding item to cart" };
    }
  },

  /**
   * Update cart item
   * @param {string} itemId - Cart item ID
   * @param {Object} data - Update data
   * @param {number} data.quantity - New quantity
   * @returns {Promise<Object>} Updated cart
   */
  updateCartItem: async (itemId, data) => {
    try {
      const response = await axiosInstance.put(`/cart/items/${itemId}`, data);
      return response.data;
    } catch (error) {
      console.error(
        "Cart service error:",
        error.response?.data || error.message
      );

      // Handle stock validation errors specially
      if (error.response?.status === 400) {
        const responseData = error.response.data;
        // Check if this is a stock validation error
        if (responseData.message === "Not enough stock available") {
          throw {
            message: responseData.message,
            availableStock: responseData.availableStock,
            requestedQuantity: responseData.requestedQuantity,
          };
        }
      }

      // If it's a proper API error response with data, forward it
      if (error.response && error.response.data) {
        throw error.response.data;
      }

      // Otherwise create a generic error
      throw { message: "Error updating cart item" };
    }
  },

  /**
   * Remove item from cart
   * @param {string} itemId - Cart item ID
   * @returns {Promise<Object>} Updated cart
   */
  removeFromCart: async (itemId) => {
    try {
      const response = await axiosInstance.delete(`/cart/items/${itemId}`);
      return response.data;
    } catch (error) {
      console.error(
        "Cart service error:",
        error.response?.data || error.message
      );
      // If it's a proper API error response with data, forward it
      if (error.response && error.response.data) {
        throw error.response.data;
      }
      // Otherwise create a generic error
      throw { message: "Error removing item from cart" };
    }
  },

  /**
   * Clears all items from the user's cart.
   * @returns {Promise<object>} The response data from the API.
   */
  clearCart: async () => {
    try {
      // The backend route for clearing the cart is DELETE /api/v1/cart
      const response = await axiosInstance.delete("/cart"); // Corrected endpoint
      console.log("cartService: Clear cart response:", response.data);
      return response.data;
    } catch (error) {
      console.error(
        "cartService: Error clearing cart:",
        error.response?.data || error.message
      );
      throw error.response?.data || new Error("Error clearing cart");
    }
  },

  /**
   * Apply coupon to cart
   * @param {string} couponCode - Coupon code
   * @returns {Promise<Object>} Updated cart with applied coupon
   */
  applyCoupon: async (couponCode) => {
    try {
      const response = await axiosInstance.post("/cart/coupon", {
        code: couponCode,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: "Error applying coupon" };
    }
  },

  /**
   * Remove coupon from cart
   * @returns {Promise<Object>} Updated cart without coupon
   */
  removeCoupon: async () => {
    try {
      const response = await axiosInstance.delete("/cart/coupon");
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: "Error removing coupon" };
    }
  },
};

export default cartService;
